import {
    Row,
    Typography,
    Button,
    message,
    Table,
    Modal,
    Input,
    Divider,
    Spin,
    Select,
    Popconfirm,
} from "antd";
import { useState, useEffect } from "react";
import { InfoCircleOutlined } from "@ant-design/icons";
import { useMediaQuery } from "react-responsive";
import HTTPClient from "../../../utils/HTTPClient";
import moment from "moment/moment";
import { useTranslation } from "react-i18next";

const AddDnsForm = ({ domain, setAdding, isLargeScreen, fetch }) => {
    const [saving, setSaving] = useState(false);
    const [type, setType] = useState("");
    const [host, setHost] = useState("");
    const [value, setValue] = useState("");
    const { t } = useTranslation();

    const saveDnsRecord = async () => {
        setSaving(true);
        const body = {
            domain: domain.domain,
            record: { type, host, value },
        };
        try {
            await HTTPClient.post("/domains/dns", body);
            message.success(t("domains.list.modal.add_dns.success"));
            fetch();
        } catch (e) {
            message.error(t("domains.list.modal.add_dns.error"));
        }
        clearInputs();
    };
    const TYPE_VALUES = ["A", "AAAA", "CNAME", "MX", "TXT", "SRV"];

    const clearInputs = () => {
        setAdding(false);
        setSaving(false);
        setHost("");
        setValue("");
        setType("");
    };

    return (
        <div
            style={{
                display: "flex",
                flexDirection: "column",
                width: isLargeScreen ? "50%" : "100%",
                gap: "10px",
            }}>
            <Input
                value={host}
                onChange={(e) => setHost(e.target.value)}
                placeholder="Host"
                size="large"
                style={{ width: "100%" }}
            />
            <Input
                value={value}
                onChange={(e) => setValue(e.target.value)}
                placeholder="Value"
                size="large"
                style={{ width: "100%" }}
            />
            <Select
                onChange={(e) => setType(e)}
                value={type}
                size="large"
                style={{ width: "100%" }}>
                {TYPE_VALUES.map((item) => (
                    <Select.Option key={item}>{item}</Select.Option>
                ))}
            </Select>
            <Row style={{ justifyContent: "flex-end", gap: "10px" }}>
                <Button onClick={() => clearInputs()} size="large" type="error" loading={saving}>
                    {t("domains.list.modal.add_dns.cancel")}
                </Button>
                <Button
                    onClick={() => saveDnsRecord()}
                    size="large"
                    type="primary"
                    loading={saving}>
                    {t("domains.list.modal.add_dns.save")}
                </Button>
            </Row>
        </div>
    );
};
const DomainInfoModal = ({ open, setOpen, domain, setDomain }) => {
    const [dns, setDns] = useState([]);
    const [toModifyRecordId, setToModifyRecordId] = useState(-1);
    const [appsAvailable, setAppsAvailable] = useState([]);
    const [appSelected, setAppSelected] = useState(null);
    const [subdomain, setSubdomain] = useState(null);
    const [linking, setLinking] = useState(false);
    const [loading, setLoading] = useState(false);
    const [adding, setAdding] = useState(false);
    const { t } = useTranslation();

    const fetch = async () => {
        setLoading(true);
        const dns_res = await HTTPClient.get("/domains/dns?domain=" + domain.domain);
        setDns(dns_res?.data);

        const apps_res = await HTTPClient.get("/app/");

        let tmp_apps = apps_res.data;

        // filter for possible_url_custom
        tmp_apps = tmp_apps.filter((app_bundle) => app_bundle?.apps.some((app) => app.url_custom));

        // filter for domain already included
        tmp_apps = tmp_apps.filter((app_bundle) => {
            const all_app_bundle_domains = [];
            app_bundle?.apps.forEach((app) => {
                if (!app?.domains) return;
                app?.domains.forEach((d) => {
                    all_app_bundle_domains.push(d.domain);
                });
            });
            return !all_app_bundle_domains.includes(domain.domain);
        });

        tmp_apps = tmp_apps.map((app_bundle) => {
            return {
                name: app_bundle.name,
                technology: app_bundle.technology,
            };
        });

        setAppsAvailable(tmp_apps);
        setLoading(false);
    };

    useEffect(() => {
        if (!domain) return;
        fetch();
    }, [domain]);

    const isLargeScreen = useMediaQuery({
        query: "(min-width: 771px)",
    });

    const changeRecordProp = (index, prop, value) => {
        const tmp_dns = [...dns];
        tmp_dns[index][prop] = value;
        setDns(tmp_dns);
    };

    const linkApp = async () => {
        setLinking(true);
        if (!appSelected) {
            message.info(t("domains.list.modal.link_app.info"));
            setLinking(false);
            return;
        }
        const app = appsAvailable.find((app) => app.name === appSelected);
        try {
            await HTTPClient.post("/domains/link_to_app", {
                domain: domain.domain,
                subdomain: subdomain || "@",
                appbundlename: app.name,
                apptechnology: app.technology,
            });
            message.success(t("domains.list.modal.link_app.success"));
            fetch();
        } catch (err) {
            const { error_code } = err?.response?.data;
            message.error(t([`errors.${error_code}`, "domains.list.modal.link_app.error"]));
        }
        setLinking(false);
    };

    const deleteRecord = async (record_id) => {
        try {
            await HTTPClient.patch("/domains/dns", {
                domain: domain.domain,
                record_id,
            });
            message.success(t("domains.list.modal.delete_record.success"));
            fetch();
        } catch (err) {
            const { error_code } = err?.response?.data;
            message.error(t([`errors.${error_code}`, "domains.list.modal.delete_record.error"]));
            message.error("Errore nell'eliminazione del record");
        }
    };

    const changeRecord = async (record_id) => {
        const record = dns.find((r) => r.record_id === record_id);
        if (!record) return;

        try {
            await HTTPClient.put("/domains/dns", {
                domain: domain.domain,
                rrid: record_id,
                rrhost: record.host,
                rrvalue: record.value,
            });
            message.success(t("domains.list.modal.change_record.success"));
            fetch();
        } catch (err) {
            const { error_code } = err?.response?.data;
            message.error(t([`errors.${error_code}`, "domains.list.modal.change_record.error"]));
            message.error("Errore nell'eliminazione del record");
        }
    };

    const APPBOX_SERVER = "49.12.113.131";

    const renderRecordProp = (_, record, index, prop) => {
        let propText = record[prop];
        if (propText === APPBOX_SERVER) {
            propText = "Server Appbox.cloud";
        }

        return record.record_id === toModifyRecordId ? (
            <Input
                type="text"
                value={record[prop]}
                onChange={(e) => changeRecordProp(index, prop, e.target.value)}
            />
        ) : (
            <Typography.Text>{propText}</Typography.Text>
        );
    };

    const dataColumns = [
        {
            title: "Host",
            dataIndex: "host",
            key: "host",
            render: (_, record, index) => renderRecordProp(_, record, index, "host"),
        },
        {
            title: "Value",
            dataIndex: "value",
            key: "value",
            render: (_, record, index) => renderRecordProp(_, record, index, "value"),
        },
        {
            title: "TTL",
            dataIndex: "ttl",
            key: "ttl",
            render: (_, record, index) => <Typography.Text>{record["ttl"]}</Typography.Text>,
        },
        {
            title: "type",
            dataIndex: "type",
            key: "type",
            render: (_, record, index) => <Typography.Text>{record["type"]}</Typography.Text>,
        },
        {
            title: "",
            dataIndex: "",
            key: "action",
            render: (_, record, index) => {
                return (
                    <>
                        {record.record_id === toModifyRecordId ? (
                            <>
                                <Button type="link" onClick={() => changeRecord(record.record_id)}>
                                    {t("domains.list.modal.add_dns.save")}
                                </Button>
                                <Button
                                    type="link"
                                    style={{ color: "red" }}
                                    onClick={() => {
                                        setToModifyRecordId(-1);
                                    }}>
                                    {t("domains.list.modal.add_dns.cancel")}
                                </Button>
                            </>
                        ) : (
                            <>
                                <Button
                                    type="link"
                                    onClick={() => {
                                        setToModifyRecordId(record.record_id);
                                    }}>
                                    {t("domains.list.modal.add_dns.edit")}
                                </Button>
                                <Popconfirm
                                    title={t("domains.list.modal.pop_confirm")}
                                    onConfirm={() => deleteRecord(record.record_id)}
                                    onCancel={() => { }}
                                    okButtonProps={{ size: "large" }}
                                    cancelButtonProps={{
                                        size: "large",
                                    }}
                                    okText={t("domains.list.modal.add_dns.delete")}
                                    cancelText={t("domains.list.modal.add_dns.cancel")}
                                    style={{ alignSelf: "right" }}>
                                    <Button type="link" style={{ color: "red" }}>
                                        {t("domains.list.modal.add_dns.delete")}
                                    </Button>
                                </Popconfirm>
                            </>
                        )}
                    </>
                );
            },
        },
    ];

    if (!domain) return null;
    if (loading)
        return (
            <Modal
                loading={loading}
                open={open}
                title={
                    <div
                        style={{
                            display: "flex",
                            justifyContent: "space-between",
                            marginTop: "15px",
                            width: "100%",
                        }}>
                        <Typography.Text strong style={{ fontSize: "x-large" }}>
                            {domain?.domain}
                        </Typography.Text>
                        <Typography.Text type="secondary">
                            {t("domains.list.modal.fields.expiration_date")}:{" "}
                            {moment(domain.expiration_date).format("DD/MM/YYYY")}
                        </Typography.Text>
                    </div>
                }
                okText={""}
                cancelText={""}
                width={isLargeScreen ? "80vw" : "100vw"}
                footer={null}
                destroyOnClose
                onCancel={() => {
                    setOpen(false);
                    setDomain(null);
                }}
                onOk={() => {
                    setOpen(false);
                    setDomain(null);
                }}>
                <div
                    style={{
                        width: "100%",
                        minHeight: "20vh",
                        overflow: "auto",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                    }}>
                    <Spin />
                </div>
            </Modal>
        );

    return (
        <Modal
            loading={loading}
            open={open}
            title={
                <div
                    style={{
                        display: "flex",
                        justifyContent: "space-between",
                        marginTop: "15px",
                        width: "100%",
                    }}>
                    <Typography.Text strong style={{ fontSize: "x-large" }}>
                        {domain.domain}
                    </Typography.Text>
                    <Typography.Text type="secondary">
                        {t("domains.list.modal.fields.expiration_date")}:{" "}
                        {moment(domain.expiration_date).format("DD/MM/YYYY")}
                    </Typography.Text>
                </div>
            }
            okText={""}
            cancelText={""}
            width={isLargeScreen ? "50vw" : "100vw"}
            footer={null}
            destroyOnClose
            onCancel={() => {
                setOpen(false);
                setDomain(null);
                setToModifyRecordId(-1);
            }}
            onOk={() => {
                setOpen(false);
                setDomain(null);
                setToModifyRecordId(-1);
            }}>
            <div style={{ width: "100%", maxHeight: "80vh", overflow: "auto" }}>
                <Divider />
                <Row>
                    <Typography.Text strong style={{ fontSize: "large" }}>
                        {t("domains.list.modal.link_domain")}
                    </Typography.Text>
                </Row>
                <Row>
                    <Typography.Text style={{ fontSize: "small", fontWeight: "lighter" }}>
                        <InfoCircleOutlined style={{ marginRight: "5px" }} />
                        {t("domains.list.modal.info")}
                        <b> {t("domains.list.modal.subdomain")}</b>
                    </Typography.Text>
                </Row>
                <Row>
                    {appsAvailable.length ? (
                        <div
                            style={{
                                width: "100%",
                                display: "flex",
                                justifyContent: "flex-start",
                                gap: "10px",
                                flexDirection: isLargeScreen ? "row" : "column",
                            }}>
                            <Select
                                value={appSelected}
                                onChange={(value) => setAppSelected(value)}
                                placeholder={t("domains.list.modal.select_app")}
                                size="large"
                                style={{
                                    marginTop: "10px",
                                    width: isLargeScreen ? "200px" : "100%",
                                }}
                                options={appsAvailable.map((app) => ({
                                    label: app.name,
                                    value: app.name,
                                }))}
                            />

                            <Input
                                placeholder={t("domains.list.modal.subdomain")}
                                value={subdomain}
                                style={{
                                    marginTop: "10px",
                                    width: isLargeScreen ? "200px" : "100%",
                                }}
                                onChange={(e) => setSubdomain(e.target.value)}
                            />
                            <Button
                                loading={linking}
                                type="primary"
                                size="large"
                                disabled={!appSelected}
                                style={{
                                    marginTop: "10px",
                                    width: isLargeScreen ? "200px" : "100%",
                                    alignSelf: "flex-end",
                                }}
                                onClick={() => linkApp()}>
                                {t("domains.list.modal.link")}
                            </Button>
                        </div>
                    ) : (
                        <Typography.Text>{t("domains.list.modal.no_apps")}</Typography.Text>
                    )}
                </Row>
                <Divider />
                <Row>
                    <Typography.Text strong style={{ fontSize: "large" }}>
                        {t("domains.list.modal.manage_dns")}
                    </Typography.Text>
                </Row>

                <Row>
                    <Table
                        pagination={false}
                        columns={dataColumns}
                        dataSource={dns}
                        style={{ marginTop: "25px", width: "100%" }}
                    />
                </Row>

                <Row
                    style={{
                        display: "flex",
                        justifyContent: "flex-end",
                        gap: "10px",
                        width: "100%",
                    }}>
                    <Button
                        type="primary"
                        style={{ marginTop: "10px" }}
                        disabled={adding}
                        onClick={() => {
                            setAdding(!adding);
                        }}>
                        {t("domains.list.modal.add_record")}{" "}
                    </Button>
                </Row>
                {adding && (
                    <AddDnsForm
                        domain={domain}
                        setAdding={setAdding}
                        isLargeScreen={isLargeScreen}
                        fetch={fetch}
                    />
                )}

                <Divider />
            </div>
        </Modal>
    );
};

export { DomainInfoModal };
